import { useState, useEffect, useRef, useMemo } from 'react';
import NoPhiNotice from '../NoPhiNotice';
import { onSnapshot, updateDoc, serverTimestamp, deleteField, runTransaction } from 'firebase/firestore';
import { httpsCallable } from '../../lib/callable';
import { db, functions } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { usePermission } from '../permissions/PermissionTooltip';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../../constants/firebase';
import { studyDesignConfigDoc, protocolAnalysisLatestDoc } from '../../constants/firestorePaths';
import { readDocVersion, docChangedMessage } from '../../utils/versionedDoc';
import { versionedDocFor } from '../../utils/versionedFirestoreDoc';
import {
  Plus, Trash2, Save, Loader2, AlertCircle, Upload, Sparkles, CheckCircle2,
  FileText, X, Edit2, Check,
} from 'lucide-react';
import { functionsUrl } from '../../config/appConfig';
import {
  scheduleUploadMode,
  protocolVisitCountOf,
  protocolLineText,
} from './scheduleUploadPanel';
import {
  PROSE_VISITS_NOTICE,
  CONFIRM_VISITS_LABEL,
  proseVisitsNeedConfirmation,
  confirmationFields,
  fieldsForVisitSave,
} from './proseVisitsNotice';

// getUploadUrlCore: lean-codebase build of the upload-URL endpoint (functions-core/).
// Host comes from build-time config, never a literal — a hardcoded host sends a
// fork's uploads into main's Storage. See config/appConfig.ts.
const GET_UPLOAD_URL = functionsUrl('getUploadUrlCore');

function vsMimeFromName(name: string): string | null {
  const lower = name.toLowerCase();
  if (lower.endsWith('.pdf')) return 'application/pdf';
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
  if (lower.endsWith('.docx'))
    return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  if (lower.endsWith('.xlsx'))
    return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  return null;
}

interface Visit {
  id: string;
  name: string;
  /** null = the protocol states no study day for this visit. */
  day: number | null;
  windowBefore: number;
  windowAfter: number;
  required: boolean;
  forms?: string[];
  /** 'protocol_text' for a visit read from the protocol's sentences (functions schedule/proseVisits.ts). */
  source?: string;
  /** The protocol sentence a prose-read visit came from. */
  sourceQuote?: string;
}

interface VisitScheduleFile {
  path: string;
  fileName?: string;
  contentType?: string;
}

interface Props {
  projectId: string;
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

export default function VisitManager({ projectId, showToast }: Props) {
  const { user } = useAuth();
  const canEdit = usePermission('edit');

  const [visits, setVisits] = useState<Visit[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [visitScheduleFile, setVisitScheduleFile] = useState<VisitScheduleFile | undefined>(
    undefined,
  );

  // The STORED words about where the visits came from and whether a person has confirmed them
  // (`visits` above may hold unsaved edits, so the stored list is counted separately).
  const [storedConfig, setStoredConfig] = useState<{ visitsSource?: unknown; visitsConfirmedAt?: unknown; visits: unknown[] }>({ visits: [] });
  const [confirming, setConfirming] = useState(false);

  // The Schedule of Events is usually in the protocol, so the upload panel only
  // shows when the protocol has none (see scheduleUploadPanel.ts).
  const [configLoaded, setConfigLoaded] = useState(false);
  const [analysisLoaded, setAnalysisLoaded] = useState(false);
  const [analysisExists, setAnalysisExists] = useState(false);
  const [protocolVisitCount, setProtocolVisitCount] = useState(0);
  const [panelRequested, setPanelRequested] = useState(false);

  // Row-level edit state
  const [editRowId, setEditRowId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Partial<Visit>>({});

  const vsFileInputRef = useRef<HTMLInputElement>(null);
  const [vsUploading, setVsUploading] = useState(false);
  const [vsExtracting, setVsExtracting] = useState(false);
  const [vsImporting, setVsImporting] = useState(false);
  const [vsPreview, setVsPreview] = useState<any>(null);

  // Guard: don't overwrite local unsaved edits when Firestore fires.
  const hasChangesRef = useRef(false);
  const setHasChangesWithRef = (v: boolean) => {
    hasChangesRef.current = v;
    setHasChanges(v);
  };

  // Optimistic concurrency: the version of studyDesign/config whose visits
  // this page holds. The Study Design page saves the same document.
  const designVersion = useMemo(
    () => (projectId ? versionedDocFor(studyDesignConfigDoc(projectId)) : null),
    [projectId],
  );

  useEffect(() => {
    if (!projectId) return;
    setConfigLoaded(false);
    const unsub = onSnapshot(studyDesignConfigDoc(projectId), (snap) => {
      if (snap.exists()) {
        const data = snap.data() as any;
        if (!hasChangesRef.current) {
          // Only here does the page take on the stored visits, so only here
          // does it record the stored version. While it holds unsaved edits
          // it keeps the version those edits were based on.
          designVersion?.observe(readDocVersion(data));
          setVisits((data.visits as Visit[]) || []);
        }
        setVisitScheduleFile((data.visitScheduleFile as VisitScheduleFile) || undefined);
        setStoredConfig({
          visitsSource: data.visitsSource,
          visitsConfirmedAt: data.visitsConfirmedAt,
          visits: Array.isArray(data.visits) ? data.visits : [],
        });
      }
      setConfigLoaded(true);
    }, () => setConfigLoaded(true));
    return () => unsub();
  }, [projectId, designVersion]);

  useEffect(() => {
    if (!projectId) return;
    setAnalysisLoaded(false);
    setAnalysisExists(false);
    setProtocolVisitCount(0);
    setPanelRequested(false);
    const unsub = onSnapshot(
      protocolAnalysisLatestDoc(projectId),
      (snap) => {
        const data = snap.exists() ? snap.data() : undefined;
        setAnalysisExists(!!data && data.status !== 'extraction_failed');
        setProtocolVisitCount(protocolVisitCountOf(data));
        setAnalysisLoaded(true);
      },
      () => setAnalysisLoaded(true),
    );
    return () => unsub();
  }, [projectId]);

  const uploadMode = scheduleUploadMode({
    loaded: configLoaded && analysisLoaded,
    analysisExists,
    protocolVisitCount,
    hasUploadedFile: !!visitScheduleFile,
  });
  const showProseNotice = proseVisitsNeedConfirmation(storedConfig);
  const showFullPanel = uploadMode === 'full' || (uploadMode === 'fromProtocol' && panelRequested);

  // ── Visit CRUD ────────────────────────────────────────────────────────────

  const updateVisitLocal = (id: string, updates: Partial<Visit>) => {
    setVisits((prev) => prev.map((v) => (v.id === id ? { ...v, ...updates } : v)));
    setHasChangesWithRef(true);
  };

  const addVisit = () => {
    const lastVisit = visits[visits.length - 1];
    const newVisit: Visit = {
      id: `v-${Date.now()}`,
      name: `Visit ${visits.length + 1}`,
      day: lastVisit && typeof lastVisit.day === 'number' ? lastVisit.day + 28 : 0,
      windowBefore: 3,
      windowAfter: 3,
      required: true,
      forms: [],
    };
    setVisits((prev) => [...prev, newVisit]);
    setHasChangesWithRef(true);
    // Open the new row in edit mode immediately
    setEditRowId(newVisit.id);
    setEditDraft({
      name: newVisit.name,
      day: newVisit.day,
      windowBefore: newVisit.windowBefore,
      windowAfter: newVisit.windowAfter,
      required: newVisit.required,
    });
  };

  const removeVisit = (id: string) => {
    if (editRowId === id) { setEditRowId(null); setEditDraft({}); }
    setVisits((prev) => prev.filter((v) => v.id !== id));
    setHasChangesWithRef(true);
  };

  const startEdit = (visit: Visit) => {
    setEditRowId(visit.id);
    setEditDraft({
      name: visit.name,
      day: visit.day,
      windowBefore: visit.windowBefore,
      windowAfter: visit.windowAfter,
      required: visit.required,
    });
  };

  const confirmEdit = (id: string) => {
    updateVisitLocal(id, editDraft);
    setEditRowId(null);
    setEditDraft({});
  };

  const cancelEdit = () => {
    setEditRowId(null);
    setEditDraft({});
  };

  const handleSave = async () => {
    if (!projectId || saving || !designVersion) return;
    setSaving(true);
    try {
      // The whole visits array, from this page's copy: claims the version the
      // copy came from plus one, and is refused if the document has moved.
      // A person who edits and saves visits read from the protocol text has checked them: that confirms them.
      const confirm = fieldsForVisitSave(storedConfig, user?.uid ?? '', serverTimestamp());
      const outcome = await designVersion.write((next) =>
        updateDoc(studyDesignConfigDoc(projectId), { visits, docVersion: next, ...confirm } as any),
      );
      if (outcome === 'saved') {
        setHasChangesWithRef(false);
        showToast('Visit schedule saved.', 'success');
      } else {
        showToast(docChangedMessage('The visit schedule'), 'error');
      }
    } catch (err: any) {
      showToast(err?.message || 'Could not save visits.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Confirm the visits read from the protocol text. One pair of fields, not this page's copy of the
  // document: read the stored version and write it plus one in a transaction, queued with this page's
  // saves, as the file upload below does.
  const handleConfirmVisits = async () => {
    if (!projectId || !user || !designVersion || confirming) return;
    setConfirming(true);
    try {
      const ref = studyDesignConfigDoc(projectId);
      await designVersion.exclusive(async () => {
        const moved = await runTransaction(db, async (tx) => {
          const before = readDocVersion((await tx.get(ref)).data());
          tx.update(ref, { ...confirmationFields(user.uid, serverTimestamp()), docVersion: before + 1 } as any);
          return { before, after: before + 1 };
        });
        designVersion.adoptIfCurrent(moved.before, moved.after);
      });
      showToast('Visits confirmed.', 'success');
    } catch (err: any) {
      showToast(err?.message || 'Could not confirm the visits.', 'error');
    } finally {
      setConfirming(false);
    }
  };

  // ── Upload handlers ───────────────────────────────────────────────────────

  const handleVisitScheduleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file || !projectId || !user || !designVersion) return;
    const contentType = vsMimeFromName(file.name);
    if (!contentType) {
      showToast('Upload a PDF, PNG, JPG, Word (.docx), or Excel (.xlsx) file.', 'error');
      return;
    }
    setVsUploading(true);
    try {
      const idToken = await user.getIdToken();
      const res = await fetch(GET_UPLOAD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({
          projectId,
          fileName: file.name,
          storagePath: 'visitSchedule',
          contentType,
        }),
      });
      if (!res.ok) throw new Error('Could not start the upload.');
      const { uploadUrl, filePath } = await res.json();
      const put = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: file,
      });
      if (!put.ok) throw new Error('Upload failed.');
      // One field, not this page's copy of the document: read the stored
      // version and write it plus one in a transaction, queued with this
      // page's saves. If nothing else had moved the document, the page's
      // copy is still current and it adopts the new version.
      const ref = studyDesignConfigDoc(projectId);
      const uploadedBy = user.uid;
      await designVersion.exclusive(async () => {
        const moved = await runTransaction(db, async (tx) => {
          const before = readDocVersion((await tx.get(ref)).data());
          tx.set(ref, {
            visitScheduleFile: {
              path: filePath,
              fileName: file.name,
              contentType,
              uploadedAt: serverTimestamp(),
              uploadedBy,
            },
            docVersion: before + 1,
          }, { merge: true });
          return { before, after: before + 1 };
        });
        designVersion.adoptIfCurrent(moved.before, moved.after);
      });
      setVsPreview(null);
      showToast('Schedule of Events document uploaded.', 'success');
    } catch (err: any) {
      showToast(err?.message || 'Upload failed.', 'error');
    } finally {
      setVsUploading(false);
    }
  };

  const handleRemoveVisitSchedule = async () => {
    if (!projectId || !designVersion) return;
    try {
      // One field: read-then-write in a transaction, as for the upload.
      const ref = studyDesignConfigDoc(projectId);
      await designVersion.exclusive(async () => {
        const moved = await runTransaction(db, async (tx) => {
          const before = readDocVersion((await tx.get(ref)).data());
          tx.update(ref, { visitScheduleFile: deleteField(), docVersion: before + 1 });
          return { before, after: before + 1 };
        });
        designVersion.adoptIfCurrent(moved.before, moved.after);
      });
      setVsPreview(null);
      showToast('Schedule of Events document removed.', 'success');
    } catch (err: any) {
      showToast(err?.message || 'Could not remove the file.', 'error');
    }
  };

  const handleExtractVisitSchedule = async () => {
    if (!projectId) return;
    setVsExtracting(true);
    setVsPreview(null);
    try {
      const fn = httpsCallable(functions, CLOUD_FUNCTIONS.EXTRACT_VISIT_SCHEDULE, {
        timeout: CALLABLE_TIMEOUTS.MEDIUM,
      });
      const result: any = await fn({ projectId });
      setVsPreview(result.data);
    } catch (err: any) {
      showToast(err?.message || 'Could not read the Schedule of Events.', 'error');
    } finally {
      setVsExtracting(false);
    }
  };

  const handleImportVisitSchedule = async () => {
    if (!projectId) return;
    setVsImporting(true);
    try {
      const fn = httpsCallable(functions, CLOUD_FUNCTIONS.EXTRACT_VISIT_SCHEDULE, {
        timeout: CALLABLE_TIMEOUTS.MEDIUM,
      });
      await fn({ projectId, commit: true });
      setHasChangesWithRef(false);
      setVsPreview(null);
      showToast('Visit schedule imported into the table.', 'success');
    } catch (err: any) {
      showToast(err?.message || 'Import failed.', 'error');
    } finally {
      setVsImporting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Visit Management</h2>
        <div className="flex items-center gap-3">
          {hasChanges && (
            <span className="text-sm text-amber-600 flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              Unsaved changes
            </span>
          )}
          <button
            onClick={addVisit}
            disabled={!canEdit}
            className="px-3 py-2 text-sm bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            <span>Add Visit</span>
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges || !canEdit}
            className="px-3 py-2 text-sm btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>Save</span>
          </button>
        </div>
      </div>

      {showProseNotice && (
        <div role="status" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <span className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {PROSE_VISITS_NOTICE}
          </span>
          <button
            onClick={handleConfirmVisits}
            disabled={confirming || hasChanges || !canEdit}
            title={hasChanges ? 'Save your changes first. Saving also confirms the visits.' : undefined}
            className="shrink-0 px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
          >
            {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            <span>{CONFIRM_VISITS_LABEL}</span>
          </button>
        </div>
      )}

      {uploadMode === 'fromProtocol' && !panelRequested && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600">
          <span className="flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            {protocolLineText(protocolVisitCount)}
          </span>
          <button
            onClick={() => setPanelRequested(true)}
            className="text-xs text-primary-700 hover:underline"
          >
            Upload a separate Schedule of Events
          </button>
        </div>
      )}

      {/* Upload panel */}
      {showFullPanel && (
      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <NoPhiNotice className="mb-3" />
        <input
          type="file"
          ref={vsFileInputRef}
          accept=".pdf,.png,.jpg,.jpeg,.docx,.xlsx"
          className="hidden"
          onChange={handleVisitScheduleFile}
        />
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Schedule of Events Document</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Upload your Schedule of Events — PDF, image, Word (.docx), or Excel (.xlsx). Use
              "Extract &amp; Import" to fill the visit table below. A PDF or image is also shown
              as-is in the CRF Completion Guidelines; Word/Excel populate the schedule table there.
            </p>
          </div>
          {!visitScheduleFile && (
            <button
              onClick={() => vsFileInputRef.current?.click()}
              disabled={vsUploading || !canEdit}
              className="shrink-0 px-3 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
            >
              {vsUploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              <span>{vsUploading ? 'Uploading…' : 'Upload'}</span>
            </button>
          )}
        </div>

        {visitScheduleFile && (
          <div className="mt-3 flex items-center justify-between gap-4 rounded-md bg-white border border-gray-200 px-3 py-2">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="h-4 w-4 text-gray-400 shrink-0" />
              <span className="text-sm text-gray-700 truncate">
                {visitScheduleFile.fileName || 'Visit schedule'}
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={handleExtractVisitSchedule}
                disabled={vsExtracting || vsImporting || !canEdit}
                className="px-3 py-1.5 text-sm bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 disabled:opacity-50 flex items-center gap-2"
              >
                {vsExtracting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                <span>{vsExtracting ? 'Reading…' : 'Extract & Import'}</span>
              </button>
              <button
                onClick={() => vsFileInputRef.current?.click()}
                disabled={vsUploading || !canEdit}
                className="px-3 py-1.5 text-sm text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                Replace
              </button>
              <button
                onClick={handleRemoveVisitSchedule}
                disabled={!canEdit}
                className="px-2 py-1.5 text-sm text-gray-400 rounded-lg hover:bg-gray-100 hover:text-red-600 disabled:opacity-50"
                title="Remove"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {vsPreview && (
          <div className="mt-3 rounded-md bg-white border border-primary-200 px-3 py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="text-sm text-gray-700">
                <p className="font-medium text-gray-900">
                  Found {vsPreview.visitCount} visit{vsPreview.visitCount === 1 ? '' : 's'} and{' '}
                  {vsPreview.formAssignmentCount} form assignment
                  {vsPreview.formAssignmentCount === 1 ? '' : 's'}.
                </p>
                {vsPreview.unmatchedForms?.length > 0 && (
                  <p className="text-xs text-amber-700 mt-1">
                    {vsPreview.unmatchedForms.length} schedule item
                    {vsPreview.unmatchedForms.length === 1 ? '' : 's'} didn&apos;t match a CRF and
                    won&apos;t be assigned: {vsPreview.unmatchedForms.join(', ')}
                  </p>
                )}
                {vsPreview.unmatchedProtocolVisits?.length > 0 && (
                  <p className="text-xs text-amber-700 mt-1">
                    {vsPreview.unmatchedProtocolVisits.length} protocol visit
                    {vsPreview.unmatchedProtocolVisits.length === 1 ? '' : 's'} aren&apos;t a column in
                    this schedule (they may be captured as forms instead):{' '}
                    {vsPreview.unmatchedProtocolVisits
                      .map((v: any) =>
                        v.likelyForm ? `${v.name} (likely the form "${v.likelyForm}")` : v.name,
                      )
                      .join(', ')}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Importing replaces the visit rows below with this schedule.
                </p>
              </div>
              <button
                onClick={() => setVsPreview(null)}
                className="text-gray-400 hover:text-gray-600 shrink-0"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={handleImportVisitSchedule}
                disabled={vsImporting}
                className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
              >
                {vsImporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" />
                )}
                <span>{vsImporting ? 'Importing…' : 'Import into table'}</span>
              </button>
              <button
                onClick={() => setVsPreview(null)}
                disabled={vsImporting}
                className="px-3 py-1.5 text-sm text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Visit table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 font-medium text-gray-700">Visit</th>
              <th className="text-left py-3 px-4 font-medium text-gray-700">Day</th>
              <th className="text-left py-3 px-4 font-medium text-gray-700">Window (−/+)</th>
              <th className="text-left py-3 px-4 font-medium text-gray-700">Required</th>
              <th className="text-right py-3 px-4 font-medium text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visits.length === 0 && (
              <tr>
                <td colSpan={5} className="py-8 text-center text-sm text-gray-400">
                  No visits defined. Add a visit
                  {showFullPanel ? ' or upload a Schedule of Events document above' : ''}.
                </td>
              </tr>
            )}
            {visits.map((visit) =>
              editRowId === visit.id ? (
                // ── Edit row ──────────────────────────────────────────────
                <tr key={visit.id} className="border-b border-primary-100 bg-primary-50/40">
                  <td className="py-2 px-4">
                    <input
                      type="text"
                      value={editDraft.name ?? ''}
                      onChange={(e) => setEditDraft((d) => ({ ...d, name: e.target.value }))}
                      autoFocus
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                    />
                  </td>
                  <td className="py-2 px-4">
                    <input
                      type="number"
                      value={editDraft.day ?? ''}
                      placeholder="TBD"
                      onChange={(e) =>
                        setEditDraft((d) => ({ ...d, day: Number.isNaN(parseInt(e.target.value)) ? null : parseInt(e.target.value) }))
                      }
                      className="w-20 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                    />
                  </td>
                  <td className="py-2 px-4">
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        value={editDraft.windowBefore ?? 0}
                        onChange={(e) =>
                          setEditDraft((d) => ({
                            ...d,
                            windowBefore: parseInt(e.target.value) || 0,
                          }))
                        }
                        className="w-12 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 text-center text-sm"
                      />
                      <span className="text-gray-400">/</span>
                      <input
                        type="number"
                        value={editDraft.windowAfter ?? 0}
                        onChange={(e) =>
                          setEditDraft((d) => ({
                            ...d,
                            windowAfter: parseInt(e.target.value) || 0,
                          }))
                        }
                        className="w-12 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 text-center text-sm"
                      />
                    </div>
                  </td>
                  <td className="py-2 px-4">
                    <input
                      type="checkbox"
                      checked={editDraft.required ?? true}
                      onChange={(e) =>
                        setEditDraft((d) => ({ ...d, required: e.target.checked }))
                      }
                      className="h-4 w-4 text-primary-600 rounded focus:ring-primary-500"
                    />
                  </td>
                  <td className="py-2 px-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => confirmEdit(visit.id)}
                        className="p-1 text-primary-600 hover:text-primary-800 rounded"
                        title="Confirm"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="p-1 text-gray-400 hover:text-gray-600 rounded"
                        title="Cancel"
                      >
                        <X className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => removeVisit(visit.id)}
                        className="p-1 text-gray-400 hover:text-red-500 rounded"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                // ── Display row ───────────────────────────────────────────
                <tr key={visit.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium text-gray-900 max-w-xs">
                    <span className="block truncate">{visit.name}</span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700">{typeof visit.day === 'number' ? `Day ${visit.day}` : 'Day TBD'}</td>
                  <td className="py-3 px-4 text-sm text-gray-700">
                    ±{visit.windowBefore}/{visit.windowAfter}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700">
                    {visit.required ? (
                      <span className="text-primary-600 font-medium">✓</span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {canEdit && (
                        <button
                          onClick={() => startEdit(visit)}
                          className="p-1 text-gray-400 hover:text-primary-600 rounded"
                          title="Edit"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                      )}
                      {canEdit && (
                        <button
                          onClick={() => removeVisit(visit.id)}
                          className="p-1 text-gray-400 hover:text-red-500 rounded"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
